import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, Tag, Button, Tooltip, ConfigProvider, Divider, Modal, Checkbox, Dropdown, Space, Drawer } from "antd";
import { ExclamationCircleOutlined, InfoCircleOutlined, CopyOutlined, SettingOutlined, DownOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en"; // 预加载英文 locale，但不全局设置
import { useMonitor } from "../context/MonitorContext";
import useFlightCopy, { DEFAULT_FLIGHT_COPY_COLUMNS, DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS } from "../hooks/useFlightCopy";
import { useTimeZone, TIMEZONE_OPTIONS, getTimezoneByMode } from "../../../../contexts/TimeZoneContext";
import FlightDateDisplay from "../../../../components/DateTimeDisplay/FlightDateDisplay";
import { MODAL_KEYS, TRANSACTION_TYPES, TRANSACTION_STATUS } from "../constants";
import { useTableSelection } from "../../../../hooks";
import { useAuth } from "../../../../hooks/useAuth";
import AircraftTag from "../../../../components/AircraftTag";
import RotationVisualizer from "./RotationVisualizer";
import EnhancedFlightTimes from "./EnhancedFlightTimes";
import TransactionCell from "./TransactionCell";
import { getCustomerColumn } from "./customerColumn";
import BatchTransactionDrawer from "../../BatchTransactionDrawer";
import EnhancedBatchActions from "./EnhancedBatchActions";
import { InlineHelpButton } from "../../../../components/common/HelpButton";
import EntityEmailPanel from "../../../mailHub/inbox/components/EntityEmailPanel";

dayjs.extend(utc);
dayjs.extend(timezone);
// 注意：不再调用 dayjs.locale("en")，避免设置全局 locale
// 复制日期需要英文月份缩写时，使用实例级 locale("en")

const FIXED_OFFSET = 280; // 定义一个常量来表示固定的偏移量
const ENABLE_MAIL_CONTEXT_COLUMN = false; // 邮件上下文入口尚未正式上线，V2 重做时再启用。

/**
 * 航班列表视图
 * 显示航班运行数据的表格视图
 */
const FlightListView = ({ airportsMap = {} }) => {
    const { user } = useAuth();
    const { timeZone: timeZoneMode } = useTimeZone();
    const { flights, loading, pagination, updatePagination, toggleModal, filters, activeView, highlightFlightIds, setHighlightFlightIds, highlightedRowId, setHighlightedRowId, refreshAndHighlightFlight } = useMonitor();
    const [tableScrollY, setTableScrollY] = useState(0);

    // 使用useMemo缓存表格数据，减少不必要的重新计算
    const tableDataSource = useMemo(() => {
        return flights || [];
    }, [flights]);

    const { selectedRowKeys, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        rowKey: "id",
        dataSource: tableDataSource,
        pagination,
        mouseMoveThrottle: 120, // 提高节流时间，减少处理频率
    });

    // 全选当前页面所有航班
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = tableDataSource.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, tableDataSource);
    }, [tableDataSource, handleRowSelectionChange]);

    // 从选中列表中移除单个航班
    const handleRemoveFlight = useCallback(
        (flightId) => {
            const newSelectedKeys = selectedRowKeys.filter((key) => key !== flightId);
            const newSelectedRows = tableDataSource.filter((flight) => newSelectedKeys.includes(flight.id));
            handleRowSelectionChange(newSelectedKeys, newSelectedRows);
        },
        [selectedRowKeys, tableDataSource, handleRowSelectionChange],
    );

    // V3.3 批量事务抽屉状态
    const [batchTransactionDrawerVisible, setBatchTransactionDrawerVisible] = useState(false);
    const [currentBatchTransactionType, setCurrentBatchTransactionType] = useState(null);

    // V3.0 批量事务抽屉状态
    const [batchTransactionDrawerV3Visible, setBatchTransactionDrawerV3Visible] = useState(false);
    const [currentBatchTransactionTypeV3, setCurrentBatchTransactionTypeV3] = useState(null);

    const [mailPanelFlight, setMailPanelFlight] = useState(null);

    const { copyConfigModalVisible, selectedCopyColumns, extendedCopyColumns, handleCopyFlights, handleCloseCopyConfig, handleCopyColumnChange, copyMenuItems } = useFlightCopy({
        defaultColumns: DEFAULT_FLIGHT_COPY_COLUMNS,
        extendedColumns: DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS,
        copyTitle: "AAE SCHEDULE (UTC TIME)",
        flights: tableDataSource,
        selectedRowKeys,
        airportsMap,
    });

    useEffect(() => {
        const updateTableHeight = () => {
            // 310px 是一个估计值，需要根据页面上表格之外的固定元素高度进行精确调整
            // 例如：页面头部、底部、表格自身的头部、分页器等
            const calculatedHeight = window.innerHeight - FIXED_OFFSET;
            setTableScrollY(calculatedHeight > 0 ? calculatedHeight : 400); // 确保高度大于0，否则给个默认值
        };

        updateTableHeight(); // Initial calculation
        window.addEventListener("resize", updateTableHeight);

        return () => {
            window.removeEventListener("resize", updateTableHeight);
        };
    }, []);

    useEffect(() => {
        if (highlightFlightIds.length > 0 && !loading && flights.length > 0) {
            const foundIds = highlightFlightIds.filter((id) => flights.some((f) => f.id === id));
            if (foundIds.length > 0) {
                setHighlightedRowId(foundIds);
                setHighlightFlightIds([]);
                requestAnimationFrame(() => {
                    const row = document.querySelector(`[data-row-key="${foundIds[0]}"]`);
                    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
                });
            }
        }
    }, [highlightFlightIds, loading, flights, setHighlightFlightIds, setHighlightedRowId]);

    // 监听筛选条件和视图变化，自动清空已选择的航班 + 清除高亮
    const prevFiltersRef = useRef(JSON.stringify(filters));
    const prevActiveViewRef = useRef(activeView);
    useEffect(() => {
        const currentFilters = JSON.stringify(filters);
        if (currentFilters !== prevFiltersRef.current || activeView !== prevActiveViewRef.current) {
            clearSelection();
            setHighlightedRowId([]);
            prevFiltersRef.current = currentFilters;
            prevActiveViewRef.current = activeView;
        }
    }, [filters, activeView, clearSelection, setHighlightedRowId]);

    // 处理航班信息编辑成功后的回调
    const handleFlightInfoEditSuccess = useCallback(
        (flightId, responseData) => {
            refreshAndHighlightFlight(flightId, responseData);
        },
        [refreshAndHighlightFlight],
    );

    // V3.3 批量事务抽屉处理函数
    const handleOpenBatchTransactionDrawer = useCallback((transactionType) => {
        setCurrentBatchTransactionType(transactionType);
        setBatchTransactionDrawerVisible(true);
    }, []);

    const handleCloseBatchTransactionDrawer = useCallback(() => {
        setBatchTransactionDrawerVisible(false);
        setCurrentBatchTransactionType(null);
    }, []);

    const handleBatchTransactionSuccess = useCallback(
        (result) => {
            // V4.0 批量事务成功处理
            if (result.changesSummary) {
                // 根据变更摘要刷新相关航班
                const affectedFlightIds = new Set();

                // 从选中的航班中获取所有航班ID
                selectedRowKeys.forEach((flightId) => {
                    affectedFlightIds.add(flightId);
                });

                // 刷新所有受影响的航班
                affectedFlightIds.forEach((flightId) => {
                    refreshAndHighlightFlight(flightId);
                });
            } else if (result.results) {
                // 兼容旧版本的结果格式
                result.results.forEach((item) => {
                    if (item.success) {
                        refreshAndHighlightFlight(item.flight_id);
                    }
                });
            } else {
                // 如果没有具体的结果信息，刷新所有选中的航班
                selectedRowKeys.forEach((flightId) => {
                    refreshAndHighlightFlight(flightId);
                });
            }
        },
        [refreshAndHighlightFlight, selectedRowKeys],
    );

    // V3.0 批量事务抽屉处理函数
    const handleOpenBatchTransactionDrawerV3 = useCallback((transactionType) => {
        setCurrentBatchTransactionTypeV3(transactionType);
        setBatchTransactionDrawerV3Visible(true);
    }, []);

    const handleCloseBatchTransactionDrawerV3 = useCallback(() => {
        setBatchTransactionDrawerV3Visible(false);
        setCurrentBatchTransactionTypeV3(null);
    }, []);

    const handleBatchTransactionSuccessV3 = useCallback(
        (result) => {
            // 批量刷新成功处理的航班
            if (result.success_results) {
                result.success_results.forEach((item) => {
                    if (item.flight_id) {
                        refreshAndHighlightFlight(item.flight_id);
                    }
                });
            }

            // 关闭抽屉
            handleCloseBatchTransactionDrawerV3();
        },
        [refreshAndHighlightFlight, handleCloseBatchTransactionDrawerV3],
    );

    // 判断航班是否存在告警 - 使用useCallback优化，避免每次渲染都重新计算
    const hasWarnings = useCallback((flight) => {
        return flight.status === "DELAYED";
    }, []);

    // 处理选择航班事件
    const handleShowDetails = useCallback(
        (flight) => {
            toggleModal(MODAL_KEYS.FLIGHT_DETAIL, true, flight);
        },
        [toggleModal],
    );

    const canViewMailContext = ENABLE_MAIL_CONTEXT_COLUMN && Boolean(user && (
        user.is_superuser
        || user.permissions?.includes("mail_hub:inbox_view")
        || user.permissions?.includes("mail_hub:mailbox_manage")
        || user.permissions?.includes("mail_hub:manage")
    ));

    const handleOpenMailPanel = useCallback((flight) => {
        if (!flight?.id) return;
        setMailPanelFlight(flight);
    }, []);

    const handleCloseMailPanel = useCallback(() => {
        setMailPanelFlight(null);
    }, []);

    // 处理分页变化
    const handleTableChange = useCallback(
        (pagination, filters, sorter) => {
            updatePagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
            });
        },
        [updatePagination],
    );

    // Function to determine row class name
    const getRowClassName = useCallback(
        (record) => {
            let className = "compact-row";
            if (highlightedRowId.includes(record.id)) {
                className += " row-updated";
            }
            return className;
        },
        [highlightedRowId],
    );

    // 自定义行属性函数 - 只允许序号列选中
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);

            // 移除原始的 onClick 事件，只保留其他属性
            const { onClick, ...restProps } = originalProps;

            return {
                ...restProps,
                // 不添加 onClick 事件，这样点击行的其他部分不会触发选中
            };
        },
        [getRowProps],
    );

    // 使用useMemo缓存表格列定义，避免不必要的重新渲染
    const columns = useMemo(
        () => [
            getIndexColumn("序号", 30, { fixed: "left" }),
            {
                title: "注册号",
                dataIndex: "aircraft_reg",
                key: "aircraft_reg",
                align: "center",
                width: 60,
                render: (text) => <AircraftTag registration={text} />,
            },
            {
                title: "ROT",
                key: "rotation",
                align: "center",
                fixed: "left",
                width: 26,
                render: (_, record) => <RotationVisualizer flight={record} allFlights={tableDataSource} />,
            },
            // booking_no 列已移至 Rotation 列的 Tooltip 中展示
            {
                title: "日期",
                dataIndex: "flight_date",
                key: "flight_date",
                align: "center",
                fixed: "left",
                width: 40,
                render: (flight_date, record) => {
                    const { etd, std, departure_airport } = record;
                    // 优先级：ETD > STD > flight_date
                    const primaryTime = etd || std;
                    return <FlightDateDisplay etdOrStd={primaryTime} departureAirportCode={departure_airport} originalFlightDateForFallback={flight_date} />;
                },
            },
            {
                title: <Tooltip title="星期">D</Tooltip>,
                key: "day_of_week",
                align: "center",
                width: 16,
                render: (_, record) => {
                    const { etd, std, departure_airport, flight_date } = record;
                    // 优先使用ETD或STD，如果都没有，则使用航班日期作为备用
                    const timeToConvert = etd || std || flight_date;

                    if (!timeToConvert) {
                        return "-"; // 如果没有有效时间，则不显示
                    }

                    try {
                        const targetTimezone = getTimezoneByMode(timeZoneMode, departure_airport, airportsMap);
                        // 使用dayjs转换时间并获取星期（.day()返回0-6，周日是0）
                        const dayOfWeek = dayjs.utc(timeToConvert).tz(targetTimezone).day();

                        // 将周日(0)映射为7，以符合常见习惯
                        return dayOfWeek === 0 ? 7 : dayOfWeek;
                    } catch (error) {
                        console.error("计算星期失败:", error, { record, timeZoneMode });
                        return "?"; // 出错时显示'?'
                    }
                },
            },
            {
                title: "航班号",
                dataIndex: "flight_number",
                key: "flight_number",
                align: "center",
                fixed: "left",
                width: 40,
                render: (text, record) => {
                    let flightNumberStyle = {};
                    if (timeZoneMode === TIMEZONE_OPTIONS.LOCAL) {
                        flightNumberStyle.color = "#722ed1"; // Purple for Local time mode
                    }
                    if (hasWarnings(record)) {
                        flightNumberStyle.color = "#faad14"; // Warning color takes precedence if both apply
                    }
                    // 检查用户是否有查看详情的权限
                    const canViewDetails = user && (user.is_superuser || user.permissions?.includes("flight_operation:read"));

                    return (
                        <Tooltip title={hasWarnings(record) ? "此航班存在待处理事项或延误（规则待确认）" : ""}>
                            {canViewDetails ? (
                                <a onClick={() => handleShowDetails(record)} style={flightNumberStyle}>
                                    {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 2, fontSize: "11px", color: flightNumberStyle.color || "#faad14" }} />}
                                    {text}
                                </a>
                            ) : (
                                <span style={flightNumberStyle}>
                                    {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 2, fontSize: "11px", color: flightNumberStyle.color || "#faad14" }} />}
                                    {text}
                                </span>
                            )}
                        </Tooltip>
                    );
                },
            },
            {
                title: "航段",
                key: "route",
                align: "center",
                fixed: "left",
                width: 56,
                render: (_, record) => {
                    const route = `${record.departure_airport} - ${record.arrival_airport}`;
                    // 备降占位段：追加备降机场标签（设计文档 §7.2）
                    if (record.status === "DIVERTED" && record.diversion_airport) {
                        return (
                            <Tooltip title={`该航班已备降至 ${record.diversion_airport} 机场`}>
                                <span>{route}</span>
                                <Tag color="purple" style={{ marginLeft: 2, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                                    {record.diversion_airport}
                                </Tag>
                            </Tooltip>
                        );
                    }
                    return route;
                },
            },
            ...(canViewMailContext
                ? [
                      {
                          title: <Tooltip title="查看当前航班关联邮件">邮件</Tooltip>,
                          key: "mail_context",
                          align: "center",
                          width: 44,
                          render: (_, record) => (
                              <Tooltip title={record.id ? "查看关联邮件" : "该航班缺少业务对象 ID"}>
                                  <Button
                                      type="text"
                                      size="small"
                                      icon={<MailOutlined />}
                                      disabled={!record.id}
                                      onClick={(event) => {
                                          event.stopPropagation();
                                          handleOpenMailPanel(record);
                                      }}
                                  />
                              </Tooltip>
                          ),
                      },
                  ]
                : []),
            {
                title: (
                    <Tooltip title="航班时间信息，点击可查看更多细节">
                        航班时间 <InfoCircleOutlined style={{ fontSize: "12px" }} />
                    </Tooltip>
                ),
                dataIndex: "flights_times",
                key: "flight_times",
                fixed: "left",
                width: 210,
                className: "flight-times-cell",
                render: (_, record) => <EnhancedFlightTimes flight={record} airportsMap={airportsMap} allFlights={tableDataSource} onFlightInfoEditSuccess={(flightId, responseData) => handleFlightInfoEditSuccess(flightId ?? record.id, responseData)} />,
            },
            {
                title: "备注",
                dataIndex: "remarks",
                key: "remarks",
                width: 160,
                ellipsis: true,
                render: (text) => {
                    if (!text) return "-";
                    return (
                        <Tooltip title={text}>
                            <span>{text.length > 20 ? text.substring(0, 20) + "..." : text}</span>
                        </Tooltip>
                    );
                },
            },
            // 客户列（只读，按 sales:flight-attribution:customer-view 门控；无权限返回空数组）
            // 列表视图是监控默认视图，客户列必须在这里也在，否则授权用户默认看不到
            ...getCustomerColumn({ user, width: 180 }),
            // 事务状态栏 - 添加各类事务状态
            // 根据用户权限决定是否显示事务相关列
            ...(user && (user.is_superuser || user.permissions?.includes("flight_operation:read") || user.permissions?.includes("flight_operation:create") || user.permissions?.includes("flight_operation:update") || user.permissions?.includes("flight_operation:delete"))
                ? [
                      {
                          title: <Tooltip title="许可申请状态">许可</Tooltip>,
                          key: "permit_request",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.PERMIT_REQUEST} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                      {
                          title: <Tooltip title="燃油申请状态">燃油</Tooltip>,
                          key: "fuel_request",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.FUEL_REQUEST} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                      {
                          title: <Tooltip title="飞行简报状态">简报</Tooltip>,
                          key: "flight_brief",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.FLIGHT_BRIEF} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                      {
                          title: <Tooltip title="配餐订单状态">配餐</Tooltip>,
                          key: "catering_order",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.CATERING_ORDER} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                      {
                          title: <Tooltip title="机组接送状态">接送</Tooltip>,
                          key: "crew_transportation",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.CREW_TRANSPORTATION} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                      {
                          title: <Tooltip title="酒店住宿状态">酒店</Tooltip>,
                          key: "hotac",
                          width: 50,
                          align: "center",
                          render: (_, record) => <TransactionCell flightId={record.id} transactionType={TRANSACTION_TYPES.HOTAC} flight={record} onSuccess={refreshAndHighlightFlight} />,
                      },
                  ]
                : []),
        ],
        [getIndexColumn, hasWarnings, handleShowDetails, canViewMailContext, handleOpenMailPanel, refreshAndHighlightFlight, handleFlightInfoEditSuccess, tableDataSource, timeZoneMode, airportsMap, user],
    );

    return (
        <div>
            {/* 批量操作栏 - sticky 定位确保滚动时可见 */}
            <div className="batch-actions-sticky-wrapper">
                <EnhancedBatchActions
                    selectedFlightIds={selectedRowKeys}
                    onSuccess={refreshAndHighlightFlight}
                    allFlightsData={tableDataSource}
                    onOpenBatchTransactionDrawer={handleOpenBatchTransactionDrawer}
                    onOpenBatchTransactionDrawerV3={handleOpenBatchTransactionDrawerV3}
                    onFlightRemove={handleRemoveFlight}
                    onSelectAll={handleSelectAllVisible}
                    onClearSelection={clearSelection}
                    copyMenuItems={copyMenuItems}
                />
            </div>

            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            rowHeight: 32,
                            padding: 4,
                            paddingXS: 2,
                            paddingSM: 4,
                        },
                    },
                }}
            >
                <div className="table-flex-cells">
                    <Table
                        ref={tableRef}
                        dataSource={tableDataSource}
                        columns={columns}
                        loading={loading}
                        rowKey="id"
                        size="small"
                        scroll={{ x: 1560, y: tableScrollY, scrollToFirstRowOnChange: false }}
                        virtual
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: [30, 50, 100, 300],
                            showQuickJumper: true,
                            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                        }}
                        onChange={handleTableChange}
                        onRow={getCustomRowProps}
                        rowSelection={{
                            ...rowSelection,
                            columnWidth: 6, // 明确设置选择框列宽
                        }}
                        rowClassName={getRowClassName}
                    />
                </div>
            </ConfigProvider>

            {/* V4 批量事务抽屉 */}
            <BatchTransactionDrawer key={currentBatchTransactionType} visible={batchTransactionDrawerVisible} onClose={handleCloseBatchTransactionDrawer} selectedFlights={tableDataSource.filter((flight) => selectedRowKeys.includes(flight.id))} transactionType={currentBatchTransactionType} onSuccess={handleBatchTransactionSuccess} />

            {ENABLE_MAIL_CONTEXT_COLUMN && (
                <Drawer
                    title={`邮件上下文${mailPanelFlight?.flight_number ? `：${mailPanelFlight.flight_number}` : ""}`}
                    placement="right"
                    width="min(760px, calc(100vw - 32px))"
                    open={Boolean(mailPanelFlight)}
                    onClose={handleCloseMailPanel}
                    destroyOnHidden
                >
                    {mailPanelFlight?.id ? (
                        <EntityEmailPanel entityType="flight_operation" entityId={String(mailPanelFlight.id)} />
                    ) : null}
                </Drawer>
            )}

            {/* 复制配置弹窗 */}
            <Modal
                title="自定义复制列"
                open={copyConfigModalVisible}
                onCancel={handleCloseCopyConfig}
                onOk={() => {
                    handleCopyFlights(true);
                    handleCloseCopyConfig();
                }}
                okText="复制选中列"
                cancelText="取消"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>选择要复制的列（已选择 {selectedRowKeys.length} 个航班）：</p>
                </div>
                <Checkbox.Group value={selectedCopyColumns} onChange={handleCopyColumnChange} style={{ width: "100%" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                        {extendedCopyColumns.map((column) => (
                            <Checkbox key={column.key} value={column.key}>
                                {column.label}
                            </Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
                <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                    <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                        <strong>使用说明：</strong>
                        <br />
                        • 复制内容为制表符分隔的表格，可直接粘贴到 Excel 或邮件中
                        <br />
                        • 默认格式基于 AAE SCHEDULE 标准
                        <br />• 时间格式：HH:mm，日期格式：DD-MMM
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default FlightListView;
